import './index.scss';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Badge, IconButton, Popover, Tooltip, Typography } from '@material-ui/core';
import { Skeleton } from '@material-ui/lab';
import { makeAllQuestionnaires } from '@actions/questionnaireActions';
import { translate } from '@utils';
import { QuestionnaireItem } from './QuestionnaireItem';
import { MgState, QuestionnaireQueryInterface } from '@types';
import { FilterPopover } from '@sharedComponents/FilterPopover';
import { LettersFilterLabel } from '@components/Pages/LettersPage/Filters/Elements/LettersFilterLabel';
import {
  questionnairesFiltersHasChanged,
  questionnairesPageFiltersCompaniesSelector,
  questionnairesPageFiltersLangSelector,
  questionnairesPageFiltersSelector,
} from '@selectors/questionnairesPage';
import { setPartialQuestionnairesFilters } from '@actions/questionnairesPageActions';
import { FilterListOffIcon } from '@images/icons';
import { initialQuestionnairesFilters } from '@reducers/initialState/questionnairesPage';

const QuestionnairesPage = () => {
  const [anchorEl, setAnchorEl] = useState(null);

  const dispatch = useDispatch();

  const { questionnaires, isQuestionnairesLoading } = useSelector(
    (state: MgState) => state.questionnaire
  );

  const filtersHasChanged = useSelector(questionnairesFiltersHasChanged);

  const questionnairesCompanies = useSelector(questionnairesPageFiltersCompaniesSelector);

  const questionnairesLanguages = useSelector(questionnairesPageFiltersLangSelector);

  const filters = useSelector(questionnairesPageFiltersSelector);

  const openFilterPopover = Boolean(anchorEl);

  const handleLettersFilterLabelClick = (event) => {
    setAnchorEl(event.target);
  };

  useEffect(() => {
    if (filters) {
      dispatch(makeAllQuestionnaires({ ...filters }));
    }
  }, [dispatch]);

  const handleFilterChange = (value: Partial<QuestionnaireQueryInterface>) => {
    setAnchorEl(null);
    dispatch(setPartialQuestionnairesFilters(value));
  };

  const clearFiltersComponent = (
    <Tooltip title={translate('clear_filters')} arrow>
      <IconButton
        onClick={() => handleFilterChange(initialQuestionnairesFilters)}
        size="small"
        color="primary"
        aria-label="filtering"
        component="div"
      >
        <FilterListOffIcon />
      </IconButton>
    </Tooltip>
  );

  return (
    <div className="questionnaire-container">
      <Typography component="h3" variant="h6" className="questionnaire-heading">
        {isQuestionnairesLoading ? (
          <Skeleton />
        ) : (
          <>
            {translate('questionnaires')}
            <div
              className="questionnaire-filter-controls"
              data-testid="questionnaire-filter-testId"
            >
              <Badge
                color="secondary"
                overlap="circular"
                variant="dot"
                invisible={!filtersHasChanged}
              >
                <div>
                  <LettersFilterLabel handleFiltersClick={handleLettersFilterLabelClick} />
                </div>
              </Badge>
              <div className="clear-filters" data-testid="questionnaire-clear-filters">
                {filtersHasChanged && clearFiltersComponent}
              </div>
            </div>
          </>
        )}
      </Typography>
      <Popover
        open={openFilterPopover}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
      >
        <FilterPopover
          handleFilterChange={handleFilterChange}
          filterCompanies={questionnairesCompanies}
          filterLanguage={questionnairesLanguages}
        />
      </Popover>
      <div className="questionnaire-list">
        {isQuestionnairesLoading ? (
          <Skeleton variant="rect" width="60vw" height="30vh" />
        ) : (
          questionnaires.map(({ id, lang, title, company }) => {
            return (
              <QuestionnaireItem key={id} id={id} lang={lang} title={title} company={company} />
            );
          })
        )}
      </div>
    </div>
  );
};

export { QuestionnairesPage };
